import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback, useContext, useEffect, useState } from 'react';
import { usePartialAppSelector } from 'shared/hooks/redux/hooks';
import { Alert, AlertTitle, Box, Button, Paper } from '@mui/material';
import { AddRounded, LocalShippingRounded } from '@mui/icons-material';
import { ErrorBoundary } from 'shared/components/error-boundary';
import brand from 'shared/app/model/branding';
import { SearchField } from 'console/shared/components/fields/search';
import { SimsTable } from 'console/sim/sim-list/table';
import { useNavigate } from 'react-router-dom';
import { Content } from 'console/sim/context';
import { getBilledSimsCount, getSimsWithStatus } from 'console/sim/utils/select-sim';
import { useNavigateTo } from 'shared/hooks/router';
import { useBox } from 'shared/hooks/utils';
function searchInSims(sims, search_value) {
    if (search_value === '') {
        return sims;
    }
    return sims.filter((sim) => ('+' + sim.msisdn).toLowerCase().indexOf(search_value) !== -1 ||
        (sim.device_name ?? '').toLowerCase().indexOf(search_value) !== -1);
}
const AddSimButton = memo(function AddSimButton() {
    const onAddNewSim = useNavigateTo('/sim/add');
    return (_jsx(Button, { color: "primary", variant: "outlined", onClick: onAddNewSim, startIcon: _jsx(AddRounded, {}), children: "\u0414\u043E\u0431\u0430\u0432\u0438\u0442\u044C \u0441\u0438\u043C-\u043A\u0430\u0440\u0442\u0443" }));
});
const OrderField = memo(function OrderField() {
    const onOpenOrderDialog = useNavigateTo('/sim/order');
    if (!brand.show_sim_order_form) {
        return null;
    }
    return (_jsxs(Alert, { color: "info", icon: false, children: [_jsx(AlertTitle, { children: _jsxs("strong", { children: ["\u0421\u0438\u043C-\u043A\u0430\u0440\u0442\u044B ", brand.name] }) }), _jsx(Button, { variant: "contained", color: "info", size: "small", onClick: onOpenOrderDialog, startIcon: _jsx(LocalShippingRounded, {}), children: "\u0423\u0437\u043D\u0430\u0442\u044C \u043F\u043E\u0434\u0440\u043E\u0431\u043D\u043E\u0441\u0442\u0438 \u0438 \u0437\u0430\u043A\u0430\u0437\u0430\u0442\u044C" })] }));
});
export const SimsList = memo(function SimsList() {
    const { sim_tariffs } = useContext(Content);
    const sims = usePartialAppSelector(getSimsWithStatus, sim_tariffs.descriptions);
    const billed_sim_count = usePartialAppSelector(getBilledSimsCount, sim_tariffs.descriptions);
    const navigate = useNavigate();
    const onOpenForeign = useCallback((msisdn) => navigate(`/sim/foreign/${msisdn}`), []);
    const onOpenBilled = useCallback(({ id, operator }) => navigate(`/sim/billed/${id}/${operator}`), []);
    const [filter_sims, setFilterSims] = useState(sims);
    const boxed_sims = useBox(sims);
    const [search, setSearch] = useState('');
    useEffect(() => {
        if (search == '') {
            setFilterSims(sims);
            boxed_sims.current = sims;
        }
    }, [sims]);
    const onSearch = useCallback((value) => {
        const mini_search_value = value.toLowerCase().trim();
        setSearch(value);
        setFilterSims(searchInSims(boxed_sims.current, mini_search_value));
    }, []);
    const onClearSearch = useCallback(() => {
        setSearch('');
        setFilterSims(boxed_sims.current);
    }, []);
    return (_jsx(ErrorBoundary, { children: _jsxs(Box, { display: "flex", flexDirection: "column", gap: 2, width: "100%", height: "100%", boxSizing: "border-box", p: 2, children: [_jsx(OrderField, {}), _jsx(AddSimButton, {}), _jsx(SearchField, { value: search, onChange: onSearch, onClear: onClearSearch, placeholder: "\u041F\u043E\u0438\u0441\u043A \u043F\u043E \u043D\u043E\u043C\u0435\u0440\u0443 \u0438\u043B\u0438 \u0443\u0441\u0442\u0440\u043E\u0439\u0441\u0442\u0432\u0443" }), _jsx(Paper, { children: _jsx(SimsTable, { sims: filter_sims, loading: sim_tariffs.loading, billed_sim_count: billed_sim_count, onOpenBilled: onOpenBilled, onOpenForeign: onOpenForeign }) })] }) }));
});
